import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MssCreateRequest,
  MssUpdateRequest,
  MssListQuery,
  MssListResponse,
  MssResponse,
  MssLegendEntryResponse,
} from '@produzionejb/types';
import { api } from './api-client';

// Modulo unico "Spedizioni" (MSS Spagna + MSF Francia). Endpoint backend
// /api/documents/spedizioni: il type del documento e' derivato dalla
// destinazione scelta alla creazione. I tipi MSS* sono riusati (l'unica
// differenza tra MSS e MSF era la destinazione).

const QK = {
  list: (q: Partial<MssListQuery>) => ['spedizioni-documents', 'list', q] as const,
  one: (id: string) => ['spedizioni-documents', 'one', id] as const,
  legend: (country: 'ES' | 'FR') => ['spedizioni-documents', 'legend', country] as const,
};

function toQueryString(q: Partial<MssListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.country) params.set('country', q.country);
  if (q.customerId) params.set('customerId', q.customerId);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useSpedizioniList(query: Partial<MssListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<MssListResponse>(`/api/documents/spedizioni${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useSpedizione(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['spedizioni-documents', 'one', 'NIL'],
    queryFn: () => api<MssResponse>(`/api/documents/spedizioni/${id}`),
    enabled: !!id,
  });
}

export function useCreateSpedizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MssCreateRequest) =>
      api<MssResponse>('/api/documents/spedizioni', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['spedizioni-documents', 'list'] });
    },
  });
}

export function useUpdateSpedizione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MssUpdateRequest) =>
      api<MssResponse>(`/api/documents/spedizioni/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['spedizioni-documents', 'list'] });
    },
  });
}

export function useCloseSpedizione(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MssResponse>(`/api/documents/spedizioni/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['spedizioni-documents', 'list'] });
      void qc.invalidateQueries({ queryKey: ['spedizioni-documents', 'legend'] });
    },
  });
}

export function useDeleteSpedizione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/documents/spedizioni/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['spedizioni-documents', 'list'] });
    },
  });
}

// M4: scan QR / pick FIFO / release line (endpoint unico, niente piu' docType)

export function useScanBox(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (qrCode: string) =>
      api<MssResponse>(`/api/documents/spedizioni/${id}/scan`, {
        method: 'POST',
        body: { qrCode },
      }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
    },
  });
}

export function usePickBoxes(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { productSkuId: string; lotPositionId?: string; qtyBoxes: number }) =>
      api<MssResponse>(`/api/documents/spedizioni/${id}/pick`, {
        method: 'POST',
        body,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
    },
  });
}

export function useReleaseLine(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      api<MssResponse>(`/api/documents/spedizioni/${id}/lines/${lineId}`, {
        method: 'DELETE',
      }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
    },
  });
}

// GET /api/finished-boxes per UI picker
export interface FinishedBoxItem {
  id: string;
  qrCode: string;
  productSkuId: string;
  productSkuCode: string;
  productSkuGrammatura: number;
  productSkuPiecesPerBox: number | null;
  productName: string;
  productDriveLegendId: number | null;
  lotPositionId: string;
  lotCode: string;
  qtyPieces: number;
  status: 'PRONTA' | 'ALLOCATA' | 'SPEDITA' | 'SMALTITA' | 'RIENTRATA';
  packagedByConCode: string | null;
  packagedAt: string;
  allocatedToDocId: string | null;
  shippedByDocId: string | null;
}

export function useFinishedBoxes(query: {
  status?: string;
  productSkuId?: string;
  search?: string;
  pageSize?: number;
}) {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.productSkuId) params.set('productSkuId', query.productSkuId);
  if (query.search) params.set('search', query.search);
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  return useQuery({
    queryKey: ['finished-boxes', 'list', query],
    queryFn: () =>
      api<{ items: FinishedBoxItem[]; total: number }>(
        `/api/finished-boxes?${params.toString()}`,
      ),
    staleTime: 5_000,
  });
}

export function useLegendEntries(country: 'ES' | 'FR' | null) {
  return useQuery({
    queryKey: country ? QK.legend(country) : ['spedizioni-documents', 'legend', 'NIL'],
    queryFn: () =>
      api<{ items: MssLegendEntryResponse[]; total: number }>(
        `/api/documents/spedizioni/legend/${country}`,
      ),
    enabled: !!country,
    staleTime: 30_000,
  });
}
